"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "@/lib/contact";

export interface UnifiedFAQItem {
  question: string;
  answer: React.ReactNode | string;
  iconName?: string; // e.g. "HiCurrencyDollar" from react-icons/hi
  value?: string;
}

interface UnifiedFAQProps {
  items: UnifiedFAQItem[];
  className?: string;
  showSupportLink?: boolean;
  supportLinkText?: string;
  supportLinkHref?: string;
}

/**
 * Unified FAQ Component
 * Standardizes FAQ presentation across the site.
 * Supports both simple string answers (via dangerous HTML) and complex ReactNode answers.
 * Optionally supports icons for questions.
 */
export default function UnifiedFAQ({
  items,
  className = "",
  showSupportLink = false,
  supportLinkText = "Still need help?",
  supportLinkHref = "/support/",
}: UnifiedFAQProps) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <>
      <Accordion type="single" collapsible className={cn("w-full space-y-4", className)}>
        {items.map((item, idx) => {
          const value = item.value || `item-${idx}`;

          return (
            <AccordionItem
              key={value}
              value={value}
              className="border-none bg-muted/30 rounded-[1.5rem] px-6 md:px-8 data-[state=open]:bg-muted/50 transition-colors"
            >
              <AccordionTrigger className="text-left text-lg font-bold hover:text-primary hover:no-underline py-6 [&[data-state=open]]:text-primary">
                {/* Simplified: Removed icon rendering logic to clean up the UI as per user request "too many icons" */}
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed pb-6 text-muted-foreground pl-2 md:pl-4">
                {typeof item.answer === "string" ? (
                  <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                ) : (
                  item.answer
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {showSupportLink && (
        <div className="mt-12 pt-12 border-t border-border/50 text-center">
          <p className="text-muted-foreground mb-6">{supportLinkText}</p>
          <Link
            href={supportLinkHref}
            className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-4 rounded-full font-bold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl"
          >
            Contact Support
          </Link>
          <p className="mt-4 text-sm text-muted-foreground">
            or email{" "}
            <a href={SUPPORT_MAILTO} className="text-primary font-semibold hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      )}
    </>
  );
}
